import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  fetchAnnouncements,
  fetchAnnouncementReads,
  fetchCalendarList,
  fetchCalendarWeek,
  fetchMemo,
  fetchMemoUnreadPins,
  fetchMemos,
  fetchProjectMemos,
  type ProjectMemoListParams,
  fetchTodoBoard,
  type MemoListParams,
  fetchAuthConfig,
  fetchCompanyLinks,
  fetchCandidates,
  fetchSyncRuns,
  fetchPortalSettings,
  fetchCustomerPermissions,
  fetchCustomers,
  fetchMe,
  fetchUsers,
  fetchProject,
  fetchProjectLinks,
  fetchProjectRelations,
  fetchProjectSuggestions,
  fetchProjects,
  type CandidateListParams,
  type ProjectListParams,
} from "./client";

export const meQueryKey = ["me"] as const;
export const projectsKey = (params: ProjectListParams = {}) =>
  ["projects", "list", params] as const;
export const projectKey = (id: string) => ["projects", "detail", id] as const;
export const projectRelationsKey = (id: string) => ["projects", "relations", id] as const;
export const projectLinksKey = (id: string) => ["projects", "links", id] as const;
export const candidatesKey = (params: CandidateListParams = {}) =>
  ["candidates", "list", params] as const;
export const projectSuggestionsKey = (id: string) => ["projects", "suggestions", id] as const;
export const customersKey = (status?: string) => ["customers", "list", status ?? "all"] as const;

export function useMe() {
  return useQuery({ queryKey: meQueryKey, queryFn: fetchMe });
}

export const USERS_KEY = ["users"] as const;
/** ユーザーマスタ(名前だけ)。名前が未設定の人が先、そのあとはよみがなの順。 */
/**
 * ユーザーマスタ。fresh: 出すたびに取り直す(共有の相手を選ぶときなど、ホームで読んだあとに
 * ログインした人も候補に出すため)。
 */
export function useUsers(enabled = true, { fresh = false }: { fresh?: boolean } = {}) {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: fetchUsers,
    enabled,
    staleTime: 60_000,
    ...(fresh ? { refetchOnMount: "always" as const } : {}),
  });
}

export function useAuthConfig() {
  return useQuery({ queryKey: ["auth", "config"], queryFn: fetchAuthConfig, staleTime: Infinity });
}

export function useProjects(params: ProjectListParams = {}) {
  return useQuery({ queryKey: projectsKey(params), queryFn: () => fetchProjects(params) });
}

export function useProject(id: string) {
  return useQuery({ queryKey: projectKey(id), queryFn: () => fetchProject(id) });
}

export function useProjectRelations(id: string) {
  return useQuery({ queryKey: projectRelationsKey(id), queryFn: () => fetchProjectRelations(id) });
}

export function useProjectLinks(id: string) {
  return useQuery({ queryKey: projectLinksKey(id), queryFn: () => fetchProjectLinks(id) });
}

/** サジェストは Backlog を読みに行くので、他のカードより長めに保持する。 */
export function useProjectSuggestions(id: string) {
  return useQuery({
    queryKey: projectSuggestionsKey(id),
    queryFn: () => fetchProjectSuggestions(id),
    staleTime: 5 * 60_000,
  });
}

export function useCustomers(status?: "ACTIVE" | "ARCHIVED") {
  return useQuery({ queryKey: customersKey(status), queryFn: () => fetchCustomers(status) });
}

export function useCustomerPermissions() {
  return useQuery({ queryKey: ["customers", "permissions"], queryFn: fetchCustomerPermissions });
}

export function useCandidates(params: CandidateListParams = {}) {
  return useQuery({ queryKey: candidatesKey(params), queryFn: () => fetchCandidates(params) });
}

export function useSyncRuns() {
  return useQuery({ queryKey: ["admin", "sync", "runs"], queryFn: fetchSyncRuns });
}

/** 全社向けカード。all=true は管理画面用(非表示・期間外も含む)。 */
export function useCompanyLinks(all = false) {
  return useQuery({
    queryKey: ["company", "links", all ? "all" : "active"],
    queryFn: () => fetchCompanyLinks(all),
  });
}

export function useAnnouncements(all = false) {
  return useQuery({
    queryKey: ["company", "announcements", all ? "all" : "visible"],
    queryFn: () => fetchAnnouncements(all),
    // ナビゲーションバーのベルは開いたままの画面にも出るので、新しいお知らせを 5 分ごとに見に行く
    ...(all ? {} : { refetchInterval: 5 * 60_000 }),
  });
}

export const ANNOUNCEMENT_READS_KEY = ["company", "announcements", "reads"] as const;
/** 本人が既読にした、いま掲載中のお知らせの ID(ベルの未読の件数)。 */
export function useAnnouncementReads() {
  return useQuery({
    queryKey: ANNOUNCEMENT_READS_KEY,
    queryFn: fetchAnnouncementReads,
    refetchInterval: 5 * 60_000,
  });
}

/** ポータル設定(全体の管理者)。変更は稀なので長めにキャッシュする。 */
export function usePortalSettings() {
  return useQuery({
    queryKey: ["admin", "settings"],
    queryFn: fetchPortalSettings,
    staleTime: 60_000,
  });
}

/** 更新後にプロジェクト関連のキャッシュをまとめて無効化する。 */
export function useInvalidate() {
  const queryClient = useQueryClient();
  return {
    projects: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
    customers: () => queryClient.invalidateQueries({ queryKey: ["customers"] }),
    candidates: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["candidates"] }),
        queryClient.invalidateQueries({ queryKey: ["projects"] }),
      ]),
    syncRuns: () => queryClient.invalidateQueries({ queryKey: ["admin", "sync"] }),
    company: () => queryClient.invalidateQueries({ queryKey: ["company"] }),
    memos: () => queryClient.invalidateQueries({ queryKey: ["memos"] }),
    /**
     * メモを削除したあと。消えたメモ自身の詳細は取り直さず(404 になり、再試行が終わるまで待たされる)、
     * キャッシュから外して、一覧だけ取り直す。
     */
    memoDeleted: (id: string) => {
      queryClient.removeQueries({ queryKey: ["memos", "detail", id], exact: true });
      return queryClient.invalidateQueries({ queryKey: ["memos", "list"] });
    },
    settings: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "settings"] }),
        queryClient.invalidateQueries({ queryKey: ["me"] }),
      ]),
  };
}

/** 本人の今日の予定(Google カレンダー)。予定は日中に変わるので、窓に戻ったときに取り直す。 */
/** YYYY-MM-DD を n 日ずらす(暦の計算だけなので、UTC で足す)。 */
export const addDays = (date: string, n: number): string =>
  new Date(Date.parse(`${date}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** その日を含む週の月曜(YYYY-MM-DD)。 */
export const weekStartOf = (date: string): string =>
  addDays(date, -((new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7));

/** 日本時間の今日(YYYY-MM-DD)。 */
export const jstToday = (): string =>
  new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Tokyo" });

const calendarWeekQuery = (weekStart: string) => ({
  queryKey: ["calendar", "week", weekStart],
  queryFn: () => fetchCalendarWeek(weekStart),
  staleTime: 60_000,
});

/**
 * その日(YYYY-MM-DD)を含む週の予定。週ごとに 1 回だけ取り、同じ週の中で日を移っても取り直さない
 * (2026-10-14)。日ごとに分けるのは表示側(eventsOnDay)。
 */
export function useCalendarWeek(date: string) {
  return useQuery({
    ...calendarWeekQuery(weekStartOf(date)),
    // 別の週へ移る間、前の表示を残す(ちらつかせない)
    placeholderData: (previous) => previous,
  });
}

/** 週の端の日を見ているとき、隣の週を先読みするまでの待ち時間(見ている週の取得を邪魔しないため)。 */
export const CALENDAR_PREFETCH_DELAY_MS = { value: 800 };

/**
 * 週の端の日を見ているとき、隣の週を先読みする(月曜なら前の週、日曜なら次の週)。
 * 1 日ずつ移っても、週をまたぐときに待たないため。それ以外の日は、同じ週の中なので要らない。
 */
export function usePrefetchCalendarWeek(shown: string | undefined) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!shown) return;
    const dow = new Date(`${shown}T00:00:00Z`).getUTCDay();
    const neighbor = dow === 1 ? addDays(shown, -1) : dow === 0 ? addDays(shown, 1) : null;
    if (!neighbor) return;
    const timer = setTimeout(
      () => void queryClient.prefetchQuery(calendarWeekQuery(weekStartOf(neighbor))),
      CALENDAR_PREFETCH_DELAY_MS.value,
    );
    return () => clearTimeout(timer);
  }, [shown, queryClient]);
}

/** メモの一覧(検索・絞り込みはサーバー側)。入力中の切り替えで画面がちらつかないよう前の結果を残す。 */
export function useMemos(params: MemoListParams) {
  return useQuery({
    queryKey: ["memos", "list", params],
    queryFn: () => fetchMemos(params),
    placeholderData: (previous) => previous,
  });
}

export function useMemoDetail(id: string | null) {
  return useQuery({
    queryKey: ["memos", "detail", id],
    queryFn: () => fetchMemo(id as string),
    enabled: id !== null,
    // 編集中に取り直して、入力した内容とずれないようにする(競合は revision で検出する)
    refetchOnWindowFocus: false,
  });
}

/**
 * プロジェクトのメモの一覧(SPEC §13.6)。ボード(ピン)と一覧の両方に使う。
 * キーは ["memos", "list", ...] の下に置き、メモの保存・削除のときにホームの一覧と一緒に取り直す。
 */
export function useProjectMemos(projectId: string, params: ProjectMemoListParams = {}) {
  return useQuery({
    queryKey: ["memos", "list", "project", projectId, params],
    queryFn: () => fetchProjectMemos(projectId, params),
    placeholderData: (previous) => previous,
  });
}

/** ボードのピンのうち、自分にとって未読・更新のものの件数(ホームの参加プロジェクト)。 */
export function useMemoUnreadPins() {
  return useQuery({
    queryKey: ["memos", "list", "unread-pins"],
    queryFn: fetchMemoUnreadPins,
  });
}

/** 一覧の行にカーソルが乗った・フォーカスが当たったときに、詳細を先に取っておく(開くのを速くする)。 */
export function usePrefetchMemo() {
  const queryClient = useQueryClient();
  return (id: string) =>
    queryClient.prefetchQuery({
      queryKey: ["memos", "detail", id],
      queryFn: () => fetchMemo(id),
      staleTime: 30_000,
    });
}

/** 選べるカレンダーの一覧。選択のポップオーバーを開いたときだけ取る。 */
export function useCalendarList(enabled: boolean) {
  return useQuery({
    queryKey: ["calendar", "list"],
    queryFn: fetchCalendarList,
    enabled,
    staleTime: 30_000,
  });
}

/** 本人の ToDo ボード。取り込み元はサーバーが 10 分ごとに取り込み直すので、窓に戻ったときに取り直す。 */
export const TODO_BOARD_KEY = ["todos", "board"] as const;
export function useTodoBoard() {
  return useQuery({ queryKey: TODO_BOARD_KEY, queryFn: fetchTodoBoard, staleTime: 60_000 });
}
